<script setup lang="ts">
import { computed, ref } from 'vue'
import * as Sentry from '@sentry/browser'
import UIKit from '@pocketprep/ui-kit'
import { examMetadataModule } from '@/store/examMetadata/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import { subscriptionModule } from '@/store/subscription/module'
import { mobileModule } from '@/store/mobile/module'
import { analyticsModule } from '@/store/analytics/module'
import { screenModule } from '@/store/screen/module'

const {
    Button: PocketButton,
    Calendar,
    Icon,
} = UIKit

const emit = defineEmits<{
    'close': []
}>()

const step = ref<'ask' | 'registered' | 'guess' | 'no-idea' | 'encouragement'>('ask')
const examDate = ref<Date | null>(null)
const isExamDateApprox = ref(false)     // whether the user indicates their exam date is an approximate guess
const isSavingExamDate = ref(false)

const currentBundle = computed(() => {
    return bundleModule.getters.getCurrentBundle()
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const currentUserExamMetadata = computed(() => {
    return userExamMetadataModule.getters.getCurrentUserExamMetadata()
})

const hasSubscription = computed(() => {
    return !!subscriptionModule.getters.getSubscriptionForExamId(currentUserExamMetadata.value?.objectId)
})

const isOOAP = computed(() => {
    return mobileModule.getters.isInArrivedFromMobileState()
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const daysUntilExam = computed(() => {
    return userExamMetadataModule.getters.getDaysUntilExam()
})

const canSaveDate = computed(() => {
    // Allow saving any selected exam date
    if (examDate.value) {
        return true
    }

    // Allow clearing a previously saved exam date
    const existingExamDate = userExamMetadataModule.getters.getScheduledExamDate()
    if (existingExamDate && !examDate.value) {
        return true
    }

    return false
})

const examDateScreenName = computed(() => {
    if (step.value === 'ask') {
        return 'Exam Date Ask - Registered?'
    } else if (step.value === 'registered') {
        return 'Exam Date Ask - When?'
    } else if (step.value === 'guess') {
        return 'Exam Date Ask - About When?'
    } else if (step.value === 'no-idea') {
        return 'Exam Date Ask - Where to Add'
    } else if (step.value === 'encouragement') {
        return 'Exam Date Ask - Success'
    } else {
        return 'Exam Date Ask'
    }
})

const goBack = () => {
    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        callToAction: 'Back',
        screenName: examDateScreenName.value,
    })

    if (step.value === 'encouragement' && !isExamDateApprox.value) {
        step.value = 'registered'
    } else if (step.value === 'encouragement' && isExamDateApprox.value) {
        step.value = 'guess'
    } else {
        step.value = 'ask'
    }
}

const skipClicked = () => {
    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        callToAction: 'Skip',
        screenName: examDateScreenName.value,
    })

    if (isOOAP.value) {
        returnToMobile()
    } else {
        emit('close')
    }
}

const examDatePlanClicked = (examDatePlan: 'registered' | 'guess' | 'no-idea' | 'never') => {
    // Capture if the user is providing an approximate exam date
    isExamDateApprox.value = examDatePlan === 'guess'

    analyticsModule.actions.amplitudeTrack('Exam Date Plan Clicked', {
        examDatePlan,
    })

    if (examDatePlan === 'registered' || examDatePlan === 'guess' || examDatePlan === 'no-idea') {
        step.value = examDatePlan
    } else {
        if (isOOAP.value) {
            returnToMobile()
        } else {
            emit('close')
        }
    }
}

const saveExamDate = async () => {
    if (isSavingExamDate.value) {
        return
    }

    isSavingExamDate.value = true
    const date = examDate.value || null

    // if date is somehow not valid, capture date and send to Sentry
    if (date && isNaN(date.getTime())) {
        Sentry.captureException(new Error(`Invalid date: ${examDate.value}`))
    } else {
        if (currentExamMetadata.value && currentBundle.value && currentUserExamMetadata.value) {
            await userExamMetadataModule.actions.setScheduledExamDate({
                scheduledExamDate: date,
                isScheduledExamDateApprox: isExamDateApprox.value,
                examNativeName: currentExamMetadata.value.nativeAppName,
                uemCreatedAt: currentUserExamMetadata.value.createdAt,
                uemObjectId: currentUserExamMetadata.value.objectId,
                bundle: currentBundle.value,
                hasSubscription: hasSubscription.value,
            })
        }
    }

    isSavingExamDate.value = false
    step.value = 'encouragement'
}

const returnToMobile = () => {
    mobileModule.actions.returnToMobileApp()

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        callToAction: 'Back to App',
        screenName: examDateScreenName.value,
    })

    emit('close')
}

const closeClicked = () => {
    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        callToAction: 'Close',
        screenName: examDateScreenName.value,
    })

    emit('close')
}
</script>

<template>
    <div class="exam-date-ask">
        <div
            v-if="breakpoint === 'black-bear'"
            class="exam-date-ask__back-or-skip"
        >
            <PocketButton
                v-if="step !== 'ask'"
                class="exam-date-ask__back-btn"
                type="tertiary"
                @click="goBack"
            >
                Back
            </PocketButton>
            <PocketButton
                v-if="step !== 'no-idea' && step !== 'encouragement'"
                class="exam-date-ask__skip-btn"
                type="tertiary"
                @click="skipClicked"
            >
                Skip
            </PocketButton>
        </div>
        <template v-else>
            <div
                v-if="step !== 'ask'"
                class="exam-date-ask__back-arrow-btn"
                tabindex="0"
                role="button"
                aria-label="Back"
                @click="goBack"
                @keydown.enter="goBack"
                @mousedown.prevent
            >
                <Icon class="exam-date-ask__back-icon" type="arrow" />
            </div>
            <div
                class="exam-date-ask__close-x-btn"
                tabindex="0"
                role="button"
                aria-label="Close"
                @click="skipClicked"
                @keydown.enter="skipClicked"
                @mousedown.prevent
            >
                <Icon class="exam-date-ask__close-icon" type="close" />
            </div>
        </template>
        <div class="exam-date-ask__ask-step">
            <div class="exam-date-ask__ask-step-content">
                <template v-if="step === 'ask'">
                    <div class="exam-date-ask__step-title">
                        Are you registered for your exam?
                    </div>
                    <div class="exam-date-ask__step-description">
                        We use this to give you <br v-if="breakpoint === 'black-bear'">better recommendations
                    </div>
                    <div class="exam-date-ask__choices">
                        <div
                            class="exam-date-ask__choice"
                            tabindex="0"
                            @click="examDatePlanClicked('registered')"
                            @keydown.enter="examDatePlanClicked('registered')"
                            @mousedown.prevent
                        >
                            I'm registered with a scheduled exam date
                        </div>
                        <div
                            class="exam-date-ask__choice"
                            tabindex="0"
                            @click="examDatePlanClicked('guess')"
                            @keydown.enter="examDatePlanClicked('guess')"
                            @mousedown.prevent
                        >
                            Not yet, but I know about when I want to take my exam
                        </div>
                        <div
                            class="exam-date-ask__choice"
                            tabindex="0"
                            @click="examDatePlanClicked('no-idea')"
                            @keydown.enter="examDatePlanClicked('no-idea')"
                            @mousedown.prevent
                        >
                            Not yet and I have no idea when
                        </div>
                        <div
                            class="exam-date-ask__choice"
                            tabindex="0"
                            @click="examDatePlanClicked('never')"
                            @keydown.enter="examDatePlanClicked('never')"
                            @mousedown.prevent
                        >
                            I'm not planning to take the exam
                        </div>
                    </div>
                </template>
                <template v-else-if="step === 'registered'">
                    <div class="exam-date-ask__step-text">
                        You're registered! It's real.
                    </div>
                    <div class="exam-date-ask__step-title">
                        When is your exam?
                    </div>
                    <Calendar
                        v-model="examDate"
                        class="exam-date-ask__calendar-input"
                        label="Exam Date"
                    />
                    <PocketButton
                        class="exam-date-ask__save-btn"
                        :disabled="!canSaveDate"
                        :is-loading="isSavingExamDate"
                        @click="saveExamDate"
                    >
                        Save Date
                    </PocketButton>
                </template>
                <template v-else-if="step === 'guess'">
                    <div class="exam-date-ask__step-text">
                        You're not registered yet.
                    </div>
                    <div class="exam-date-ask__step-title">
                        When are you hoping to take your exam?
                    </div>
                    <div class="exam-date-ask__step-description">
                        Next week? Month? Give us a ballpark.
                    </div>
                    <Calendar
                        v-model="examDate"
                        class="exam-date-ask__calendar-input"
                        label="Exam Date"
                    />
                    <PocketButton
                        class="exam-date-ask__save-btn"
                        :disabled="!examDate"
                        :is-loading="isSavingExamDate"
                        @click="saveExamDate"
                    >
                        Save Date
                    </PocketButton>
                </template>
                <template v-else-if="step === 'no-idea'">
                    <div class="exam-date-ask__step-text">
                        Not sure, no problem.
                    </div>
                    <div
                        class="exam-date-ask__step-title"
                        :class="{
                            'exam-date-ask__step-title--small': breakpoint === 'black-bear',
                        }"
                    >
                        You can add an exam date later on the Stats Tab
                    </div>
                    <div class="exam-date-ask__step-description">
                        Look for “Add Exam Countdown”.
                    </div>
                    <img
                        v-if="breakpoint !== 'black-bear'"
                        class="exam-date-ask__help-image-web"
                        src="@/assets/exam-date/add-exam-date-web.png"
                    >
                    <img
                        v-else
                        class="exam-date-ask__help-image-mobile"
                        src="@/assets/exam-date/add-exam-date-mobile.png"
                    >
                    <template v-if="isOOAP">
                        <PocketButton
                            class="exam-date-ask__back-to-app-btn"
                            @click="returnToMobile"
                        >
                            Back to App
                        </PocketButton>
                    </template>
                    <template v-else>
                        <PocketButton
                            class="exam-date-ask__close-btn"
                            @click="closeClicked"
                        >
                            Close
                        </PocketButton>
                    </template>
                </template>
                <template v-else-if="step === 'encouragement'">
                    <img
                        v-if="breakpoint === 'black-bear'"
                        class="exam-date-ask__background-highlight-mobile"
                        src="@/assets/exam-date/background-highlight-mobile.svg"
                    >
                    <img
                        v-else
                        class="exam-date-ask__background-highlight"
                        src="@/assets/exam-date/background-highlight.svg"
                    >
                    <div v-if="daysUntilExam !== null" class="exam-date-ask__step-text">
                        {{ daysUntilExam }} day{{ daysUntilExam === 1 ? '' : 's' }} away!
                    </div>
                    <div class="exam-date-ask__step-title">
                        <template v-if="daysUntilExam !== null && daysUntilExam < 10">
                            Your final push!
                        </template>
                        <template v-else>
                            You've got this.
                        </template>
                    </div>
                    <div class="exam-date-ask__step-description exam-date-ask__step-description--encouragement">
                        <template v-if="daysUntilExam !== null && daysUntilExam < 10">
                            Check your inbox for some tips to <br v-if="breakpoint === 'black-bear'">
                            boost your confidence and focus <br v-if="breakpoint === 'black-bear'">
                            in your final week of studying.
                        </template>
                        <template v-else>
                            Check out your inbox for tips and <br v-if="breakpoint === 'black-bear'">
                            strategies to make your study <br v-if="breakpoint === 'black-bear'">
                            time more effective.
                        </template>
                    </div>
                    <img

                        class="exam-date-ask__brain-cape-image"
                        src="@/assets/brain/brain-cape.png"
                    >
                    <template v-if="isOOAP">
                        <PocketButton
                            class="exam-date-ask__back-to-app-btn"
                            @click="returnToMobile"
                        >
                            Back to App
                        </PocketButton>
                    </template>
                    <template v-else>
                        <PocketButton
                            class="exam-date-ask__encouragement-close-btn"
                            @click="closeClicked"
                        >
                            Close
                        </PocketButton>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.exam-date-ask {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;

    &__back-or-skip {
        width: 100%;
        display: flex;
        justify-content: space-between;
        position: absolute;
        top: 0;
        padding: 25px 35px 0;
        box-sizing: border-box;
        z-index: 2;
    }

    &__skip-btn {
        margin-left: auto;
    }

    &__back-arrow-btn {
        position: absolute;
        transform: rotate(180deg);
        left: 15px;
        top: 19px;
        z-index: 10;
        color: $brand-blue;
        width: 22px;
        height: 17px;
        cursor: pointer;
        user-select: none;
        outline: none;

        &:hover {
            color: $brand-black;
        }

        &:focus::before {
            content: '';
            left: -1px;
            top: -1px;
            width: 100%;
            height: 100%;
            position: absolute;
            border: 1px solid $brand-blue;
            border-radius: 5px;
        }

        svg {
            width: 100%;
            height: 100%;
        }
    }

    &__close-x-btn {
        position: absolute;
        right: 14px;
        top: 14px;
        z-index: 10;
        color: $brand-blue;
        width: 28px;
        height: 28px;
        cursor: pointer;
        user-select: none;
        outline: none;

        &:hover {
            color: $brand-black;
        }

        &:focus::before {
            content: '';
            left: -1px;
            top: -1px;
            width: 100%;
            height: 100%;
            position: absolute;
            border: 1px solid $brand-blue;
            border-radius: 5px;
        }

        svg {
            width: 100%;
            height: 100%;
        }
    }

    &__ask-step {
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;
        overflow-x: hidden;
        overflow-y: auto;
        z-index: 1;
    }

    &__ask-step-content {
        margin-top: 78px;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &__step-text {
        color: $ash;
        text-align: center;
        font-size: 13px;
        line-height: 18px;
        margin-bottom: 12px;
    }

    &__step-title {
        color: $brand-black;
        text-align: center;
        font-size: 26px;
        font-weight: 700;
        line-height: 36px;
        max-width: 600px;

        &--small {
            font-size: 20px;
            line-height: 27px;
        }

        @include breakpoint(black-bear) {
            max-width: 315px;
        }
    }

    &__step-description {
        margin-top: 12px;
        color: $brand-black;
        text-align: center;
        font-size: 15px;
        line-height: 20px;
        width: 360px;

        @include breakpoint(black-bear) {
            width: 315px;
        }

        &--encouragement {
            margin-top: 12px;

            @include breakpoint(black-bear) {
                margin-top: 27px;
            }
        }
    }

    &__help-image-web {
        max-width: 95%;
        width: 930px;
        margin-top: 55px;
        margin-bottom: 43px;
    }

    &__help-image-mobile {
        width: 326px;
        margin-top: 30px;
        margin-bottom: 56px;
    }

    &__background-highlight {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        top: 305px;
        width: 803px;
        z-index: -1;
    }

    &__background-highlight-mobile {
        position: absolute;
        top: 330px;
        width: 751px;
        z-index: -1;
    }

    &__brain-cape-image {
        width: 400px;
        margin-top: -65px;
        margin-bottom: -50px;
        z-index: -1;
    }

    &__choices {
        margin-top: 64px;
        width: 100%;
        max-width: 492px;

        @include breakpoint(black-bear) {
            margin-top: 51px;
            max-width: 315px;
        }
    }

    &__choice {
        display: flex;
        padding: 13px 15px;
        line-height: 21px;
        margin-bottom: 12px;
        border-radius: 6px;
        background: $white;
        box-sizing: border-box;
        position: relative;
        outline: none;
        cursor: pointer;

        &::after {
            content: '';
            position: absolute;
            top: -1px;
            bottom: -1px;
            left: -1px;
            right: -1px;
            border: 0.5px solid rgba($pewter, 0.85);
            box-shadow: 0 1px 4px 0 rgba($ash, 0.3);
            border-radius: 6px;
            pointer-events: none;
        }

        @media (hover: hover) {
            &:hover::after {
                display: block;
                top: -2px;
                bottom: -2px;
                left: -2px;
                right: -2px;
                border: 1.5px solid $slate;
                border-radius: 7px;
            }
        }

        &:focus::after {
            display: block;
            border: 1px solid $brand-blue;
        }
    }

    &__calendar-input {
        width: 315px;
        margin-top: 60px;

        @include breakpoint(black-bear) {
            margin-top: 50px;
        }
    }

    &__save-btn {
        margin-top: 96px;
        margin-bottom: 60px;

        @include breakpoint(black-bear) {
            position: absolute;
            bottom: 79px;
            left: 50%;
            width: 230px;
            transform: translateX(-50%);
        }
    }

    &__back-to-app-btn {
        @include breakpoint(black-bear) {
            width: 230px;
        }
    }

    &__close-btn {
        margin-bottom: 60px;

        @include breakpoint(black-bear) {
            width: 230px;
        }
    }

    &__encouragement-close-btn {
        position: absolute;
        bottom: 126px;
        margin-bottom: 0;

        @include breakpoint(black-bear) {
            width: 230px;
            bottom: 54px;
        }
    }
}
</style>
